import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { listEnvelopes, voidEnvelope, type Envelope, type ListingRecipient } from '../api'
import { ConfirmSheet } from '../components/ConfirmSheet'
import { dateTime } from '../format'
import { endedWord } from './EnvelopeDetail'

/**
 * The filters over the list. There was a fifth, Drafts, and it could never
 * show a row: nothing writes a draft (the status has been in the schema since
 * the first migration, and sending again is this same form, prefilled, rather
 * than a saved draft). A filter that is always empty is a dead control.
 */
type Filter = 'all' | 'waiting' | 'opened' | 'completed'

function statusLabel(status: Envelope['status']): string {
  switch (status) {
    case 'completed': return 'Signed'
    case 'sent': return 'Pending'
    case 'draft': return 'Draft'
    case 'declined': return 'Declined'
    case 'voided': return 'Voided'
  }
}

function statusClass(status: Envelope['status']): string {
  switch (status) {
    case 'completed': return 'doc-status-signed'
    case 'sent': return 'doc-status-viewed'
    case 'draft': return 'doc-status-draft'
    default: return 'doc-status-neutral'
  }
}

function matchesFilter(e: Envelope, filter: Filter): boolean {
  if (filter === 'all') return true
  if (filter === 'waiting') return e.status === 'sent'
  if (filter === 'opened') return isOpened(e)
  if (filter === 'completed') return e.status === 'completed'
  return true
}

/** Sent, and at least one person has opened or signed. */
function isOpened(e: Envelope): boolean {
  return e.status === 'sent' && (e.recipients ?? []).some((r) => r.status === 'viewed' || r.status === 'signed')
}

/**
 * One person's chip on a document's row, read with the document's status.
 *
 * Every status but signed, opened and declined read "waiting", whatever the
 * document's status: a withdrawn or declined document said "Loy Chen ·
 * waiting" while its own page says "withdrawn" or "closed", and nobody waits
 * on it, since the store reminds and signs only while it is out (review 6,
 * web-6). On such a document a person who did not finish reads the page's
 * word (endedWord), and one who opened it keeps "opened", each in no state's
 * ink, as the app's whoChipOn does. Nobody waits for a copy recipient, who
 * reads "gets a copy", and a person asked only to open it who has not reads
 * "to open", as the signer's page says them (partyState).
 */
export function whoChip(r: ListingRecipient, envelopeStatus: string): { label: string; className: string } {
  if (r.status === 'signed') return { label: 'signed', className: 'who-chip who-chip-signed' }
  if (r.status === 'declined') return { label: 'declined', className: 'who-chip who-chip-declined' }
  const ended = endedWord(envelopeStatus)
  if (ended) return { label: r.status === 'viewed' ? 'opened' : ended, className: 'who-chip who-chip-voided' }
  if (r.status === 'viewed') return { label: 'opened', className: 'who-chip who-chip-opened' }
  if (r.role === 'cc') return { label: 'gets a copy', className: 'who-chip who-chip-voided' }
  if (r.role === 'needs_to_view') return { label: 'to open', className: 'who-chip who-chip-waiting' }
  return { label: 'waiting', className: 'who-chip who-chip-waiting' }
}

function signerProgress(recipients: ListingRecipient[] | undefined): string | null {
  const signers = (recipients ?? []).filter((r) => r.role === 'signer' || r.role === 'witness')
  if (signers.length === 0) return null
  const done = signers.filter((r) => r.status === 'signed').length
  return `${done} of ${signers.length} signed`
}

function ProgressNote({ recipients }: { recipients?: ListingRecipient[] }) {
  const progress = signerProgress(recipients)
  if (!progress) return null
  return <> · {progress}</>
}

export function Dashboard() {
  const [envelopes, setEnvelopes] = useState<Envelope[] | null>(null)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  // What the sender typed, and what the server was last asked. They differ
  // while somebody is still typing, which is why the search runs on submit
  // rather than on every keystroke: a LIKE over the whole list per character
  // is work the database does on somebody's behalf for no answer they wanted.
  const [typed, setTyped] = useState('')
  const [query, setQuery] = useState('')
  // Which documents are selected, and whether the withdraw sheet is open.
  // Only ones still out can be selected: a completed document's signatures are
  // real and a declined or withdrawn one has ended, so offering them would be
  // offering an action that can only be refused.
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [withdrawing, setWithdrawing] = useState(false)

  const load = useCallback(() => {
    listEnvelopes(query)
      .then((r) => {
        setEnvelopes(r.envelopes ?? [])
        // Cleared on every load: the rows have changed underneath, and a
        // selection that outlives the list it was made from is a selection of
        // whatever happens to be there now.
        setPicked(new Set())
      })
      .catch((e) => setError(e.message))
  }, [query])
  useEffect(() => {
    load()
  }, [load])

  const counts = useMemo(() => {
    const list = envelopes ?? []
    return {
      all: list.length,
      waiting: list.filter((e) => e.status === 'sent').length,
      opened: list.filter(isOpened).length,
      completed: list.filter((e) => e.status === 'completed').length,
      active: list.filter((e) => e.status === 'sent' || e.status === 'completed').length,
    }
  }, [envelopes])

  const visible = useMemo(
    () => (envelopes ?? []).filter((e) => matchesFilter(e, filter)),
    [envelopes, filter],
  )

  return (
    <main className="page">
      <div className="row-between page-head">
        <div>
          <div className="page-label">Your documents</div>
          <h1>Envelopes</h1>
          <p>Documents you have sent, and where each one stands.</p>
        </div>
        <Link className="btn" to="/new">Send a document</Link>
      </div>

      {error && <div className="notice notice-bad">{error}</div>}

      {envelopes === null && !error && <p className="muted">Loading…</p>}

      {/* Two different empty states. "Nothing sent yet" is an invitation and
          "nothing matched" is a dead end, and telling somebody who has two
          hundred documents that they have never sent one is the wrong
          sentence at the wrong moment. */}
      {envelopes?.length === 0 && !query && (
        <div className="card empty">
          <h2>Nothing sent yet</h2>
          <p>Upload a PDF, drop a signature box on it, and send it for signing.</p>
          <Link className="btn" to="/new">Send your first document</Link>
        </div>
      )}

      {withdrawing && (
        <WithdrawMany
          ids={[...picked]}
          onDone={() => {
            setWithdrawing(false)
            load()
          }}
          onClose={() => setWithdrawing(false)}
        />
      )}

      {envelopes?.length === 0 && query && (
        <div className="card empty">
          <h2>No document matches that</h2>
          <p>
            Nothing here is titled like &ldquo;{query}&rdquo;. The search looks at titles only,
            not at who a document was sent to.
          </p>
          <button
            className="btn"
            onClick={() => {
              setTyped('')
              setQuery('')
            }}
          >
            Show everything
          </button>
        </div>
      )}

      {!!envelopes?.length && (
        <>
          <div className="stats-strip">
            <div className="stat-card">
              <strong>{counts.active}</strong>
              <span>Active documents</span>
            </div>
            <div className="stat-card">
              <strong>{counts.waiting}</strong>
              <span>Waiting on signature</span>
            </div>
            <div className="stat-card">
              <strong>{counts.completed}</strong>
              <span>Signed</span>
            </div>
          </div>

          <form
            className="row search-row"
            onSubmit={(e) => {
              e.preventDefault()
              setQuery(typed)
            }}
          >
            <label className="sr-only" htmlFor="envelope-search">Search documents by title</label>
            <input
              id="envelope-search"
              type="search"
              placeholder="Search by title"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
            <button className="btn btn-secondary" type="submit">Search</button>
            {query && (
              <button
                className="btn btn-ghost"
                type="button"
                onClick={() => {
                  setTyped('')
                  setQuery('')
                }}
              >
                Clear
              </button>
            )}
          </form>

          <FilterTabs counts={counts} filter={filter} onFilter={setFilter} />

          <div className="doc-card">
            <div className="doc-card-header">
              <h2>Recent</h2>
              {picked.size > 0 ? (
                <div className="row">
                  <span className="faint small">{picked.size} selected</span>
                  <button className="btn btn-ghost small" onClick={() => setPicked(new Set())}>
                    Clear
                  </button>
                  <button className="btn btn-danger small" onClick={() => setWithdrawing(true)}>
                    Withdraw {picked.size}
                  </button>
                </div>
              ) : (
                <span className="faint small">{visible.length} shown</span>
              )}
            </div>
            <div className="doc-rows">
              {visible.map((e) => (
                /* The checkbox is a SIBLING of the link, not inside it: a
                   control nested in an anchor is clicked and navigates, and
                   the whole row is a link because opening a document is what
                   people do to it ninety-nine times out of a hundred. */
                <div key={e.id} className="doc-line">
                  {e.status === 'sent' && (
                    <span className="doc-pick">
                      {/* The name is on the input, not in a visually hidden
                          span beside it. A span repeating the title puts the
                          document's own words in the DOM twice, which makes
                          every "click the document called X" ambiguous — it
                          broke `make edges` the moment it existed. */}
                      <input
                        type="checkbox"
                        aria-label={`Select the document titled ${e.title}`}
                        checked={picked.has(e.id)}
                        onChange={(ev) => {
                          const next = new Set(picked)
                          if (ev.target.checked) next.add(e.id)
                          else next.delete(e.id)
                          setPicked(next)
                        }}
                      />
                    </span>
                  )}
                  {e.status !== 'sent' && <span className="doc-pick" aria-hidden />}
                <Link to={`/envelopes/${e.id}`} className="doc-row">
                  <div className="doc-icon" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </div>
                  <div className="doc-text">
                    <div className="doc-title">{e.title}</div>
                    <div className="doc-meta faint small">
                      {dateTime(e.created_at)}
                      {' · '}
                      {e.page_count} page{e.page_count === 1 ? '' : 's'}
                      {/*
                        Why this document is on your list. Two different things
                        put it there: you are in the organization that sent it,
                        or you are a party to it. They carry different
                        expectations — a colleague's document you can read, and
                        one you were asked to sign — and a list that mixes them
                        silently invites you to assume the first.
                      */}
                      {e.via === 'participation' && <> · <span className="tag">you are a signer</span></>}
                      {e.via === 'membership' && <> · <span className="tag">sent by your organization</span></>}
                      <ProgressNote recipients={e.recipients} />
                    </div>
                  </div>
                  <span className={`doc-status ${statusClass(e.status)}`}>{statusLabel(e.status)}</span>
                  {(e.recipients ?? []).length > 0 && (
                    <div className="who-chips">
                      {e.recipients!.map((r, i) => {
                        const chip = whoChip(r, e.status)
                        return (
                          <span key={`${r.name}-${i}`} className={chip.className}>
                            {r.name} · {chip.label}
                          </span>
                        )
                      })}
                    </div>
                  )}
                  <svg className="doc-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </Link>
                </div>
              ))}
              {visible.length === 0 && (
                <p className="muted small doc-rows-empty">No envelopes match this filter.</p>
              )}
            </div>
          </div>
        </>
      )}
    </main>
  )
}

/** The filters, each with how many of the list it would show. */
export function FilterTabs({
  counts,
  filter,
  onFilter,
}: {
  counts: Record<Filter, number>
  filter: Filter
  onFilter: (next: Filter) => void
}) {
  return (
    <div className="filter-tabs" role="tablist" aria-label="Filter envelopes">
      {([
        ['all', 'All'],
        ['waiting', 'Waiting'],
        ['opened', 'Opened'],
        ['completed', 'Completed'],
      ] as const).map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={filter === key}
          className={`filter-tab${filter === key ? ' is-active' : ''}`}
          onClick={() => onFilter(key)}
        >
          {label}<span className="filter-count">{counts[key]}</span>
        </button>
      ))}
    </div>
  )
}

/**
 * Withdrawing several documents at once.
 *
 * One reason for all of them, because that is what makes it one decision
 * rather than a list of them — "we are re-issuing the November batch" is the
 * sentence somebody actually has. Each document still gets its own event in
 * its own chain carrying that reason and who gave it: the chain has no notion
 * of a batch and should not learn one.
 *
 * They are withdrawn one at a time, in order, and the count of what succeeded
 * is reported even when one fails. A partial result is the honest outcome of
 * several independent acts, and hiding it behind "something went wrong" would
 * leave somebody guessing which half happened.
 */
function WithdrawMany({
  ids,
  onDone,
  onClose,
}: {
  ids: string[]
  onDone: () => void
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  return (
    <ConfirmSheet
      title={`Withdraw ${ids.length} document${ids.length === 1 ? '' : 's'}`}
      confirmLabel={`Withdraw ${ids.length}`}
      busy={busy}
      error={error}
      onClose={onClose}
      onConfirm={async () => {
        if (!reason.trim()) {
          setError(
            ids.length === 1
              ? 'Say why you are withdrawing it. The reason goes into the record.'
              : 'Say why you are withdrawing them. The reason goes into each record.',
          )
          return
        }
        setBusy(true)
        setError('')
        let done = 0
        try {
          for (const id of ids) {
            await voidEnvelope(id, reason.trim())
            done += 1
          }
          onDone()
        } catch (e) {
          const what = e instanceof Error ? e.message : 'One of them could not be withdrawn.'
          setError(
            done === 0
              ? what
              : `${done} of ${ids.length} withdrawn, then it stopped: ${what} Close this to see where it got to.`,
          )
        } finally {
          setBusy(false)
        }
      }}
    >
      <p>
        Signing links for {ids.length === 1 ? 'this document' : `these ${ids.length} documents`} stop
        working immediately, including for anyone signing now. The existing record is kept.
      </p>
      <p>
        This cannot be undone. To ask for signatures again, send{' '}
        {ids.length === 1 ? 'it' : 'them'} again.
      </p>
      <label htmlFor="withdraw-many-reason">
        Why are you withdrawing {ids.length === 1 ? 'it' : 'them'}?
      </label>
      <input
        id="withdraw-many-reason"
        type="text"
        autoFocus
        placeholder="Superseded by the November revision"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <p className="small faint">
        {ids.length === 1
          ? 'This is written into the record, beside your name.'
          : 'The same reason is written into each record, beside your name.'}
      </p>
    </ConfirmSheet>
  )
}
